/**
 * JINS STANDARD LP — vanilla rebuild
 * Replaces JP source's GSAP + ScrollTrigger + SplitType bundle.
 * No animation libraries — IntersectionObserver + CSS transitions only,
 * per project standard (GSAP/ScrollTrigger pinning conflicts with Dawn).
 *
 * Ports:
 *  - .inview        fade 0→1 on scroll, once, ~80% viewport trigger
 *  - .split          char-level staggered rise-in reveal on scroll (~90% trigger)
 *  - .js-hover-roll   desktop-only hover text-roll (CSS-driven, JS only wires structure)
 *  - in-page anchor scroll with focus management
 */
(function () {
  'use strict';

  var reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  var hoverCapable = window.matchMedia('(hover: hover) and (pointer: fine)');

  // ---------- .inview ----------
  function initInview(root) {
    var targets = root.querySelectorAll('.inview');
    if (!targets.length) return;

    if (reducedMotion.matches) {
      targets.forEach(function (el) { el.classList.add('is-inview'); });
      return;
    }

    var observer = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-inview');
          observer.unobserve(entry.target);
        }
      });
    }, { threshold: 0, rootMargin: '0px 0px -20% 0px' }); // ~80% viewport

    targets.forEach(function (el) { observer.observe(el); });
  }

  // ---------- .split (char- or word-level reveal) ----------
  // Walks the element's text nodes in place so child elements (e.g. <span class="en">)
  // and <br> survive. Default mode is per-character; add data-split="word" for long copy
  // (reveals one word at a time instead). Multi-word text keeps real spaces between nowrap
  // .word spans so it still wraps at word boundaries.
  // Accessibility: the original text is kept in a visually hidden span and the generated
  // pieces are aria-hidden, so screen readers read the sentence, not individual letters.
  function makePiece(text, isWord, chars) {
    var span = document.createElement('span');
    span.className = isWord ? 'word char' : 'char';
    span.textContent = text;
    span.setAttribute('aria-hidden', 'true');
    chars.push(span);
    return span;
  }

  function splitInto(node, chars, wordMode) {
    Array.prototype.slice.call(node.childNodes).forEach(function (child) {
      if (child.nodeType === 3) {
        var text = child.textContent;
        if (!text.trim()) return;
        var frag = document.createDocumentFragment();
        var multiWord = /\s/.test(text.trim());
        if (wordMode) {
          text.split(/(\s+)/).forEach(function (part) {
            if (!part) return;
            if (/^\s+$/.test(part)) { frag.appendChild(document.createTextNode(' ')); return; }
            frag.appendChild(makePiece(part, true, chars));
          });
        } else if (multiWord) {
          text.split(/(\s+)/).forEach(function (part) {
            if (!part) return;
            if (/^\s+$/.test(part)) { frag.appendChild(document.createTextNode(' ')); return; }
            var word = document.createElement('span');
            word.className = 'word';
            word.setAttribute('aria-hidden', 'true');
            Array.prototype.forEach.call(part, function (ch) {
              var c = document.createElement('span');
              c.className = 'char';
              c.textContent = ch;
              chars.push(c);
              word.appendChild(c);
            });
            frag.appendChild(word);
          });
        } else {
          Array.prototype.forEach.call(text.trim(), function (ch) {
            frag.appendChild(makePiece(ch, false, chars));
          });
        }
        node.replaceChild(frag, child);
      } else if (child.nodeType === 1 && child.tagName !== 'BR') {
        splitInto(child, chars, wordMode);
      }
    });
  }

  function splitChars(el) {
    if (el.dataset.splitInitialized === 'true') return [];
    el.dataset.splitInitialized = 'true';

    var label = el.textContent.replace(/\s+/g, ' ').trim();
    var chars = [];
    splitInto(el, chars, el.getAttribute('data-split') === 'word');

    if (chars.length && label) {
      var sr = document.createElement('span');
      sr.textContent = label;
      sr.setAttribute('style',
        'position:absolute;width:1px;height:1px;margin:-1px;padding:0;border:0;' +
        'overflow:hidden;clip:rect(0 0 0 0);white-space:nowrap;');
      el.insertBefore(sr, el.firstChild);
    }
    return chars;
  }

  function initSplit(root) {
    var targets = root.querySelectorAll('.split');
    if (!targets.length) return;

    targets.forEach(function (el) {
      var chars = splitChars(el);
      if (!chars.length) return;

      if (reducedMotion.matches) {
        chars.forEach(function (c) { c.classList.add('is-revealed'); });
        return;
      }

      var step = el.getAttribute('data-split') === 'word' ? 0.08 : 0.05;
      chars.forEach(function (c, i) {
        c.style.transitionDelay = (i * step) + 's';
      });

      var observer = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) {
            chars.forEach(function (c) { c.classList.add('is-revealed'); });
            observer.unobserve(entry.target);
          }
        });
      }, { threshold: 0, rootMargin: '0px 0px -10% 0px' }); // ~90% viewport

      observer.observe(el);
    });
  }

  // ---------- .js-hover-roll ----------
  // CSS handles the actual roll animation (transform + transition on .char).
  // JS only needs to confirm chars exist and mark the hook as wired for a11y/perf.
  function initHoverRoll(root) {
    if (reducedMotion.matches || !hoverCapable.matches) return;

    var hooks = root.querySelectorAll('.js-hover-roll');
    hooks.forEach(function (hook) {
      if (hook.dataset.splitHoverInitialized === 'true') return;
      hook.dataset.splitHoverInitialized = 'true';

      var targets = hook.matches('.js-hover-roll-target')
        ? [hook]
        : Array.prototype.slice.call(hook.querySelectorAll('.js-hover-roll-target'));

      var hasChars = targets.some(function (t) {
        return t.querySelectorAll('.char').length > 0;
      });
      if (!hasChars) return;

      targets.forEach(function (t) {
        var chars = t.querySelectorAll('.char');
        chars.forEach(function (c, i) {
          c.style.setProperty('--roll-i', i);
        });
      });

      hook.classList.add('is-hover-roll-ready');
    });
  }

  // ---------- in-page anchor scroll ----------
  function JinsStandardPage(root) {
    this.root = root;
    this.mobile = window.matchMedia('(max-width: 767px)');

    this.onClick = function (event) {
      if (event.defaultPrevented || event.button !== 0 || event.metaKey ||
          event.ctrlKey || event.shiftKey || event.altKey) return;

      var element = event.target;
      if (!(element instanceof Element)) return;

      var link = element.closest('a[href^="#"]');
      if (!link || !root.contains(link)) return;

      var href = link.getAttribute('href');
      if (!href || href === '#') return;

      var target = document.getElementById(href.slice(1));
      if (!target || !root.contains(target)) return;

      event.preventDefault();

      var configuredGap = Number(root.dataset.anchorGap || '80');
      var gap = this.mobile.matches ? 16 : (isFinite(configuredGap) ? configuredGap : 80);
      var top = target.getBoundingClientRect().top + window.pageYOffset - gap;

      var previousTabIndex = target.getAttribute('tabindex');
      target.setAttribute('tabindex', '-1');
      target.focus({ preventScroll: true });
      target.addEventListener('blur', function () {
        if (previousTabIndex === null) target.removeAttribute('tabindex');
        else target.setAttribute('tabindex', previousTabIndex);
      }, { once: true });

      window.scrollTo({
        top: Math.max(0, top),
        behavior: reducedMotion.matches ? 'auto' : 'smooth'
      });
    }.bind(this);

    root.addEventListener('click', this.onClick);
  }

  function init() {
    document.querySelectorAll('[data-jinsstandard]').forEach(function (root) {
      initInview(root);
      initSplit(root);
      initHoverRoll(root);
      new JinsStandardPage(root);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();
